import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { Insights, KeyboardArrowLeft, KeyboardArrowRight, ViewList } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { RangeType, useAsu } from "@framework/provider-asu";
import { IUserDto, useUser } from "@framework/provider-user";
import { useApiCall } from "@framework/react-hooks";
import { normalizeUserMultiAnalysisSettings } from "@framework/types";

import { EmptyPlaceholder } from "../../../../components/common/empty-placeholder";
import { useMultiAnalysis } from "../../../../context/multi-analysis-context";
import { SELECTED_ITEM_TYPE, getSelectedItemType } from "../../../../utils";
import { FaultFrequenciesPanel } from "../scheme/components/analytics/components/fault-frequencies-panel";
import { MultiAnalysisGraphicAreaPanel } from "./components/graphic-area";
import { MultiAnalysisPanel } from "./components/panel";
import { MultiAnalysisTemplatesPanel } from "./components/templates-panel";
import { MultiAnalysisTimePanelPanel } from "./components/time-panel";
import { MultiAnalysisTrendsPanel } from "./components/trends-panel";
import { TrendsTemplateScalarsEditor } from "./components/trends-panel/template-scalars-editor";
import {
  MultiAnalysisPanelResizeHandle,
  useMultiAnalysisMainPanelsLayout,
} from "./hooks/use-multi-analysis-main-panels-layout";
import { MultiAnalysisToolbar } from "./components/toolbar";
import { useMultiAnalysisFaultFrequencies } from "./hooks/use-multi-analysis-fault-frequencies";
import { useMultiAnalysisFrequencyProfileSeries } from "./hooks/use-multi-analysis-frequency-profile-series";
import { useMultiAnalysisTrendSeries, useTemplateScalarTrendSeries } from "./hooks/use-multi-analysis-trend-series";
import { useTrainRotationDirection } from "./hooks/use-train-rotation-direction";
import { MULTI_ANALYSIS_MEASURE_TYPE, ROTATION_DIRECTION } from "./types";
import type { INormalizedAnalysisTemplate } from "./utils/analysis-template-settings";
import {
  normalizeAnalysisTemplate,
  resolveActiveMeasureWindow,
  resolveAppliedAnalysisTemplate,
} from "./utils/analysis-template-settings";
import { buildTrendMeasureListFingerprint, getTrendSeriesMeasureKey } from "./utils/trend-measure-list";
import { toggleTimePanelMeasureKey } from "./utils/time-panel-measures";
import { getMultiAnalysisMainPanelSx } from "./utils/main-panels-layout";

const PINNED_PANELS_BG = "#fafaff";

const graphicAreaPanelSx: SxProps<Theme> = {
  flex: "1 1 auto",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
};

const graphicMainPanelSx: SxProps<Theme> = {
  minWidth: 0,
  display: "flex",
  gap: 1,
  alignItems: "stretch",
  overflow: "hidden",
};

const toFingerprintDate = (value: Date | string | null | undefined): string | null =>
  value instanceof Date ? value.toISOString() : value ?? null;

export const MultiAnalysis: FC = () => {
  const { formatMessage } = useIntl();
  const user = useUser<IUserDto>();
  const { isLoading, selectedItem, userSelectedRangeType, startTimestamp, endTimestamp } = useAsu();
  const {
    selectedPoints,
    frameRange,
    activeMeasureWindow,
    activeMeasureType,
    appliedTemplateId,
    effectiveDataMarks,
    toolbarState,
    setToolbarState,
    dataMarks,
    isTemplateEditMode,
    draftTemplate,
    setDraftTemplate,
  } = useMultiAnalysis();
  const [trendPanelCollapsed, setTrendPanelCollapsed] = useState(false);
  const [selectedTrendMeasureKeys, setSelectedTrendMeasureKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [canConvertActiveSignalUnits, setCanConvertActiveSignalUnits] = useState(false);
  const rotationDirectionTrainIdRef = useRef<number | null>(null);
  const [templatesCollapsed, setTemplatesCollapsed] = useState(false);
  const [analysisTemplates, setAnalysisTemplates] = useState<INormalizedAnalysisTemplate[]>([]);

  const globalTimeRange = useMemo(
    () => ({
      start: startTimestamp,
      end: endTimestamp,
    }),
    [endTimestamp, startTimestamp],
  );

  const activeMeasureFilters = useMemo(() => {
    const template = resolveAppliedAnalysisTemplate(analysisTemplates, appliedTemplateId);
    const resolvedWindow = resolveActiveMeasureWindow(activeMeasureWindow, template);
    const cell = template?.settings.cells.find(item => item.id === resolvedWindow?.cellId);

    return cell?.filters ?? {};
  }, [analysisTemplates, activeMeasureWindow, appliedTemplateId]);
  const multiAnalysisSettings = useMemo(
    () => normalizeUserMultiAnalysisSettings(user.profile.interfaceSettings.multiAnalysis),
    [user.profile.interfaceSettings.multiAnalysis],
  );
  const showTrendsPanel =
    "showTrendsPanel" in multiAnalysisSettings ? multiAnalysisSettings.showTrendsPanel !== false : true;
  const configureTemplateFiltersManually = multiAnalysisSettings.configureTemplateFiltersManually;
  const templateForScalars = useMemo(
    () =>
      isTemplateEditMode && draftTemplate
        ? draftTemplate
        : resolveAppliedAnalysisTemplate(analysisTemplates, appliedTemplateId),
    [analysisTemplates, appliedTemplateId, draftTemplate, isTemplateEditMode],
  );

  const selectedItemType = getSelectedItemType(selectedItem);
  const isTrainSelected = selectedItemType === SELECTED_ITEM_TYPE.train;
  const trainId = selectedItem && "TrainId" in selectedItem ? Number(selectedItem.TrainId) : null;
  const trainName = selectedItem && "TrainName" in selectedItem ? selectedItem.TrainName ?? "" : "";
  const {
    panelsContainerRef,
    panelFlexGrow,
    panelHeights,
    hasMeasuredLayout,
    handleTimeGraphicResizeDelta,
    handleGraphicTrendsResizeDelta,
    handleResetPanelProportions,
    isGraphicTrendsResizeEnabled,
  } = useMultiAnalysisMainPanelsLayout({
    enabled: isTrainSelected,
    trendPanelCollapsed,
    trendPanelVisible: showTrendsPanel,
  });

  const { series: scalarTrendSeries, isLoading: isScalarTrendSeriesLoading } = useTemplateScalarTrendSeries({
    enabled: isTrainSelected && !isTemplateEditMode,
    trainId: Number.isFinite(trainId) ? trainId : null,
    points: selectedPoints,
    range: globalTimeRange,
    scalarMeasures: templateForScalars?.settings.scalarMeasures ?? [],
    parameterUnits: templateForScalars?.settings.parameterUnits ?? [],
  });
  const { series: trendSeries, isLoading: isTrendSeriesLoading } = useMultiAnalysisTrendSeries({
    enabled: isTrainSelected,
    points: selectedPoints,
    range: globalTimeRange,
    measureType: activeMeasureType,
    cellFilters: activeMeasureFilters,
  });
  const isFrequencyProfile =
    activeMeasureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM &&
    toolbarState.frequencyProfile &&
    toolbarState.frequencyProfileSelection != null;
  const { series: frequencyProfileSeries, isLoading: isFrequencyProfileLoading } =
    useMultiAnalysisFrequencyProfileSeries({
      enabled: isFrequencyProfile,
      nativeX: toolbarState.frequencyProfileSelection?.nativeX ?? null,
      dataMarks,
      baseSeries: trendSeries,
      signalDisplayUnits: toolbarState.signalDisplayUnits,
    });
  const trendsPanelSeries = useMemo(
    () => [...(isFrequencyProfile ? frequencyProfileSeries : trendSeries), ...scalarTrendSeries],
    [frequencyProfileSeries, isFrequencyProfile, scalarTrendSeries, trendSeries],
  );
  const hiddenTrendMeasureKeys = useMemo(
    () =>
      new Set(
        selectedTrendMeasureKeys.size === 0
          ? []
          : [...trendSeries, ...scalarTrendSeries]
              .map(getTrendSeriesMeasureKey)
              .filter((key): key is string => key != null && !selectedTrendMeasureKeys.has(key)),
      ),
    [scalarTrendSeries, selectedTrendMeasureKeys, trendSeries],
  );
  const isTrendsPanelLoading =
    isTrendSeriesLoading || (isFrequencyProfile && isFrequencyProfileLoading) || isScalarTrendSeriesLoading;
  const trendMeasureSelectionFingerprint = useMemo(
    () =>
      JSON.stringify({
        measures: buildTrendMeasureListFingerprint([...trendSeries, ...scalarTrendSeries]),
        displayedMeasures: buildTrendMeasureListFingerprint(trendsPanelSeries),
        rangeStart: toFingerprintDate(frameRange.start),
        rangeEnd: toFingerprintDate(frameRange.end),
        activeMeasureType,
      }),
    [activeMeasureType, frameRange.end, frameRange.start, scalarTrendSeries, trendSeries, trendsPanelSeries],
  );
  const handleToggleTrendMeasureVisibility = useCallback((measureKey: string) => {
    setSelectedTrendMeasureKeys(previous => toggleTimePanelMeasureKey(previous, measureKey));
  }, []);

  const handleFaultFrequenciesPanelOpenChange = useCallback(
    (open: boolean) => {
      setToolbarState({ faultFrequencies: open });
    },
    [setToolbarState],
  );

  const {
    faultFrequencies,
    selectedFaultLineKeys,
    setSelectedFaultLineKeys,
    selectedFaultFrequenciesValues,
    faultLineColorsByKey,
    isFaultFrequenciesLoading,
    toggleFaultFrequenciesPanel,
    showFaultFrequenciesPanel,
  } = useMultiAnalysisFaultFrequencies({
    enabled: isTrainSelected,
    trainId,
    isPanelOpen: toolbarState.faultFrequencies,
    onPanelOpenChange: handleFaultFrequenciesPanelOpenChange,
    effectiveDataMarks,
    activeMeasureType,
  });

  const {
    trainRotationDirection,
    lockedToolbarDirection,
    isRotationDirectionLocked,
    isLoading: isTrainRotationDirectionLoading,
  } = useTrainRotationDirection({
    enabled: isTrainSelected,
    trainId,
  });

  useEffect(() => {
    if (!isTrainSelected || trainId === null) {
      rotationDirectionTrainIdRef.current = null;
      return;
    }

    const trainChanged = rotationDirectionTrainIdRef.current !== trainId;
    rotationDirectionTrainIdRef.current = trainId;

    if (isTrainRotationDirectionLoading) {
      if (trainChanged && toolbarState.rotationDirection !== ROTATION_DIRECTION.UNDEFINED) {
        setToolbarState({ rotationDirection: ROTATION_DIRECTION.UNDEFINED });
      }
      return;
    }

    if (lockedToolbarDirection === null && !trainChanged) {
      return;
    }

    const nextRotationDirection = lockedToolbarDirection ?? ROTATION_DIRECTION.UNDEFINED;

    if (toolbarState.rotationDirection !== nextRotationDirection) {
      setToolbarState({ rotationDirection: nextRotationDirection });
    }
  }, [
    isTrainSelected,
    isTrainRotationDirectionLoading,
    lockedToolbarDirection,
    setToolbarState,
    toolbarState.rotationDirection,
    trainId,
  ]);

  const { fn: loadAnalysisTemplates, isLoading: isTemplatesLoading } = useApiCall(
    async api => {
      try {
        const response = (await api.fetchJson({
          url: "/multi-analysis/templates",
          method: "GET",
        })) as unknown;
        const templates = Array.isArray(response) ? response.map(normalizeAnalysisTemplate) : [];

        setAnalysisTemplates(templates);
      } catch (error) {
        console.error(error);
        setAnalysisTemplates([]);
      }
    },
    { success: false, error: false },
  );
  const loadAnalysisTemplatesRef = useRef(loadAnalysisTemplates);
  const loadedTemplatesTrainIdRef = useRef<number | null>(null);

  useEffect(() => {
    loadAnalysisTemplatesRef.current = loadAnalysisTemplates;
  }, [loadAnalysisTemplates]);

  useEffect(() => {
    if (!isTrainSelected || trainId === null) {
      loadedTemplatesTrainIdRef.current = null;
      setAnalysisTemplates([]);
      return;
    }

    if (loadedTemplatesTrainIdRef.current === trainId) {
      return;
    }

    loadedTemplatesTrainIdRef.current = trainId;
    void loadAnalysisTemplatesRef.current();
  }, [isTrainSelected, trainId]);

  useEffect(() => {
    setSelectedTrendMeasureKeys(new Set());
  }, [trendMeasureSelectionFingerprint]);

  useEffect(() => {
    if (activeMeasureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM) {
      return;
    }

    if (!toolbarState.frequencyProfile && toolbarState.frequencyProfileSelection == null) {
      return;
    }

    setToolbarState({ frequencyProfile: false, frequencyProfileSelection: null });
  }, [activeMeasureType, setToolbarState, toolbarState.frequencyProfile, toolbarState.frequencyProfileSelection]);

  return (
    <ProgressOverlay
      isLoading={isLoading && userSelectedRangeType !== RangeType.RECORDER_MODE}
      wrapperSx={{ height: "100%", minHeight: 0 }}
    >
      {!isTrainSelected ? (
        <EmptyPlaceholder
          icon={<Insights />}
          text={formatMessage({ id: "pages.multiAnalysis.stationPlaceholder" })}
          wrapperProps={{ sx: { minHeight: "100%", backgroundColor: "#fafaff" } }}
        />
      ) : (
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              lg: templatesCollapsed ? "minmax(0, 1fr) 42px" : "minmax(0, 1fr) 300px",
            },
            gap: 1,
            p: { xs: 1, md: 1.5 },
            bgcolor: "#fafaff",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              overflow: "hidden",
            }}
          >
            <MultiAnalysisPanel hideHeader sx={{ flex: "0 0 auto", bgcolor: PINNED_PANELS_BG, zIndex: 1 }}>
              <MultiAnalysisToolbar
                onFaultFrequenciesToggle={() => {
                  void toggleFaultFrequenciesPanel();
                }}
                isFaultFrequenciesLoading={isFaultFrequenciesLoading}
                isRotationDirectionLocked={isRotationDirectionLocked}
                canConvertSignalUnits={canConvertActiveSignalUnits}
              />
            </MultiAnalysisPanel>

            <Box
              ref={panelsContainerRef}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <MultiAnalysisTimePanelPanel
                sx={getMultiAnalysisMainPanelSx({
                  panel: "time",
                  hasMeasuredLayout,
                  panelHeights,
                  panelFlexGrow,
                })}
                globalRange={globalTimeRange}
                trendSeries={trendSeries}
                isTrendSeriesLoading={isTrendSeriesLoading}
                activeMeasureFilters={activeMeasureFilters}
                isTrainSelected={isTrainSelected}
              />

              <MultiAnalysisPanelResizeHandle
                onResizeDelta={handleTimeGraphicResizeDelta}
                onDoubleClickReset={handleResetPanelProportions}
              />

              <Box
                sx={getMultiAnalysisMainPanelSx({
                  panel: "graphic",
                  hasMeasuredLayout,
                  panelHeights,
                  panelFlexGrow,
                  sx: graphicMainPanelSx,
                })}
              >
                <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <MultiAnalysisGraphicAreaPanel
                    templates={analysisTemplates}
                    isLoadingTemplates={isTemplatesLoading}
                    trainName={trainName}
                    range={frameRange}
                    appliedTemplateId={appliedTemplateId}
                    hiddenMeasureKeys={hiddenTrendMeasureKeys}
                    fillAvailableHeight
                    faultFrequencies={selectedFaultFrequenciesValues}
                    trainRotationDirection={trainRotationDirection}
                    onActiveSignalUnitsAvailabilityChange={setCanConvertActiveSignalUnits}
                    sx={graphicAreaPanelSx}
                  />
                </Box>
                {showFaultFrequenciesPanel ? (
                  <FaultFrequenciesPanel
                    items={faultFrequencies ?? []}
                    selectedKeys={selectedFaultLineKeys}
                    onChangeSelected={setSelectedFaultLineKeys}
                    lineColorsByKey={faultLineColorsByKey}
                    loading={isFaultFrequenciesLoading}
                  />
                ) : null}
              </Box>

              {showTrendsPanel ? (
                <>
                  <MultiAnalysisPanelResizeHandle
                    disabled={!isGraphicTrendsResizeEnabled}
                    onResizeDelta={handleGraphicTrendsResizeDelta}
                    onDoubleClickReset={handleResetPanelProportions}
                  />

                  <MultiAnalysisTrendsPanel
                    series={trendsPanelSeries}
                    range={frameRange}
                    collapsed={trendPanelCollapsed}
                    onCollapsedChange={setTrendPanelCollapsed}
                    hiddenMeasureKeys={hiddenTrendMeasureKeys}
                    onToggleMeasureVisibility={handleToggleTrendMeasureVisibility}
                    isLoading={isTrendsPanelLoading}
                    frequencyHz={isFrequencyProfile ? toolbarState.frequencyProfileSelection?.frequencyHz : null}
                    editContent={
                      isTemplateEditMode && draftTemplate ? (
                        <TrendsTemplateScalarsEditor
                          configureFiltersManually={configureTemplateFiltersManually}
                          points={selectedPoints}
                          range={globalTimeRange}
                          trainId={Number.isFinite(trainId) ? trainId : null}
                          scalarMeasures={draftTemplate.settings.scalarMeasures}
                          parameterUnits={draftTemplate.settings.parameterUnits}
                          onScalarMeasuresChange={scalarMeasures => {
                            setDraftTemplate(previous =>
                              previous
                                ? {
                                    ...previous,
                                    settings: {
                                      ...previous.settings,
                                      scalarMeasures,
                                    },
                                  }
                                : previous,
                            );
                          }}
                          onParameterUnitsChange={parameterUnits => {
                            setDraftTemplate(previous =>
                              previous
                                ? {
                                    ...previous,
                                    settings: {
                                      ...previous.settings,
                                      parameterUnits,
                                    },
                                  }
                                : previous,
                            );
                          }}
                        />
                      ) : undefined
                    }
                    expandedFlexGrow={panelFlexGrow.trends}
                    sx={getMultiAnalysisMainPanelSx({
                      panel: "trends",
                      hasMeasuredLayout,
                      panelHeights,
                      panelFlexGrow,
                      expanded: !trendPanelCollapsed,
                    })}
                  />
                </>
              ) : null}
            </Box>
          </Box>

          <MultiAnalysisPanel
            titleId="pages.multiAnalysis.templates.title"
            icon={<ViewList fontSize="small" />}
            sx={{ minHeight: 0, display: { xs: "none", lg: "flex" } }}
            actions={
              <IconButton size="small" onClick={() => setTemplatesCollapsed(value => !value)}>
                {templatesCollapsed ? <KeyboardArrowLeft fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
              </IconButton>
            }
          >
            {templatesCollapsed ? null : (
              <MultiAnalysisTemplatesPanel
                templates={analysisTemplates}
                isLoading={isTemplatesLoading}
                onReloadTemplates={async () => {
                  if (trainId === null) {
                    return;
                  }

                  loadedTemplatesTrainIdRef.current = null;
                  await loadAnalysisTemplatesRef.current();
                  loadedTemplatesTrainIdRef.current = trainId;
                }}
              />
            )}
          </MultiAnalysisPanel>
        </Box>
      )}
    </ProgressOverlay>
  );
};
